import { DWORKS_API_BASE } from "../constants/dworksConfig";

const REPORT_WINDOW_MS = 60 * 1000;
const REPORT_LIMIT = 5;
const DEDUPE_WINDOW_MS = 30 * 1000;
const recentReports = [];
const fingerprints = new Map();

function safeText(value, maxLength = 120) {
  return String(value || "")
    .replace(/[\r\n\t]+/g, " ")
    .replace(/bearer\s+[a-z0-9._~-]+/gi, "Bearer [redacted]")
    .replace(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi, "[email]")
    .replace(/(?:\+?82[- ]?)?0?1[016789][- ]?\d{3,4}[- ]?\d{4}/g, "[phone]")
    .slice(0, maxLength);
}

function safeRoute(value) {
  const raw = String(value || "/").split("?")[0].split("#")[0];
  return safeText(
    raw
      .replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, "[id]")
      .replace(/\/[a-z0-9_-]{20,}(?=\/|$)/gi, "/[id]"),
    160
  ) || "/";
}

function getErrorStatus(error) {
  const status = Number(error?.status || error?.statusCode || error?.response?.status || 0);
  return Number.isFinite(status) ? status : 0;
}

function getErrorCode(error) {
  return safeText(error?.code || (getErrorStatus(error) ? `http_${getErrorStatus(error)}` : "unknown"), 80);
}

function shouldReport(error, source) {
  if (error?.name === "AbortError") return false;
  const status = getErrorStatus(error);
  if (source === "api" && status > 0 && status < 500) return false;
  return true;
}

function consumeReportSlot(fingerprint) {
  const now = Date.now();
  while (recentReports.length && now - recentReports[0] > REPORT_WINDOW_MS) {
    recentReports.shift();
  }
  const lastSeen = fingerprints.get(fingerprint) || 0;
  if (now - lastSeen < DEDUPE_WINDOW_MS || recentReports.length >= REPORT_LIMIT) {
    return false;
  }
  recentReports.push(now);
  fingerprints.set(fingerprint, now);
  if (fingerprints.size > 100) {
    for (const [key, timestamp] of fingerprints) {
      if (now - timestamp > DEDUPE_WINDOW_MS) fingerprints.delete(key);
    }
  }
  return true;
}

export function reportClientError(error, context = {}) {
  const source = safeText(context.source || "app", 40);
  if (!shouldReport(error, source)) return;

  const route = safeRoute(context.route || (typeof window !== "undefined" ? window.location.pathname : "/"));
  const errorName = safeText(error?.name || "Error", 60);
  const errorCode = getErrorCode(error);
  const status = getErrorStatus(error);
  const fingerprint = [source, route, errorName, errorCode, status].join(":");
  if (!consumeReportSlot(fingerprint)) return;

  const payload = {
    source,
    route,
    error_name: errorName,
    error_code: errorCode,
    status,
    app_version:
      process.env.REACT_APP_GIT_SHA ||
      process.env.REACT_APP_VERSION ||
      "web",
    online: typeof navigator === "undefined" ? true : navigator.onLine !== false,
    source_file: safeRoute(context.sourceFile || ""),
    line: Number(context.line || 0),
    column: Number(context.column || 0),
    related_request_id: safeText(error?.requestId || context.requestId || "", 80)
  };

  fetch(`${DWORKS_API_BASE}/api/client-errors`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    keepalive: true
  }).catch(() => undefined);
}

export function installGlobalErrorReporting() {
  if (typeof window === "undefined") return () => undefined;

  const handleError = event => {
    reportClientError(event?.error || new Error("window_error"), {
      source: "window",
      sourceFile: event?.filename || "",
      line: event?.lineno || 0,
      column: event?.colno || 0
    });
  };
  const handleUnhandledRejection = event => {
    const reason = event?.reason;
    reportClientError(reason instanceof Error ? reason : new Error("unhandled_rejection"), {
      source: "promise"
    });
  };

  window.addEventListener("error", handleError);
  window.addEventListener("unhandledrejection", handleUnhandledRejection);

  return () => {
    window.removeEventListener("error", handleError);
    window.removeEventListener("unhandledrejection", handleUnhandledRejection);
  };
}
